/**
 * Mirrors `apps/api/src/modules/delivery/http/delivery.controller.ts` (`ShipmentView`, `ShipmentListItemView`) and
 * `delivery-shipment.service.ts` (`ShipmentDetail`). Statuses, codes and amounts are shown as the API sends them;
 * nothing here derives a meaning the API does not give (the carrier's amounts in particular: decision D4).
 */

export const SHIPMENT_TABS = [
  'in_progress',
  'to_send',
  'failed',
  'delivered',
  'returns',
  'attention',
] as const;
export type ShipmentTab = (typeof SHIPMENT_TABS)[number];

export type ShipmentStatus =
  | 'pending_dispatch'
  | 'dispatch_failed'
  | 'registered'
  | 'pickup_scheduled'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'return_in_progress'
  | 'returned'
  | 'under_review'
  | 'cancelled'
  | 'lost'
  | 'unknown';

export type AttentionReason =
  | 'under_review'
  | 'cancelled'
  | 'lost'
  | 'unknown_status'
  | 'unexpected_transition'
  | 'credentials'
  | 'return'
  | 'cancellation_refused';

export interface Shipment {
  readonly id: string;
  readonly orderId: string;
  readonly status: ShipmentStatus;
  readonly carrierStatus: string | null;
  readonly carrierStatusAt: string | null;
  readonly trackingNumber: string | null;
  readonly externalReference: string | null;
  readonly dispatchSequence: number;
  readonly attentionReason: AttentionReason | null;
  readonly cancellationStatus: 'REQUESTED' | 'REFUSED' | null;
  readonly cancellationReason: string | null;
  readonly carrierReason: string | null;
  readonly cityOverride: string | null;
  readonly attemptCount: number;
  readonly lastError: string | null;
  readonly nextPollAt: string | null;
  readonly lastPolledAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ShipmentListItem extends Shipment {
  readonly orderNumber: string;
  readonly orderStatus: string;
  readonly orderTotal: string;
  readonly recipientName: string;
  readonly governorate: string;
  readonly delegation: string;
}

export interface ShipmentPage {
  readonly items: readonly ShipmentListItem[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly counts: Record<ShipmentTab, number>;
}

export type EventSource =
  'carrier_poll' | 'carrier_notification' | 'create_response' | 'system' | 'operator';

export interface ShipmentEvent {
  readonly id: string;
  readonly source: EventSource;
  readonly carrierStatus: string | null;
  readonly status: ShipmentStatus;
  readonly occurredAt: string | null;
  readonly occurredAtRaw: string | null;
  readonly observedAt: string;
  readonly location: string | null;
}

export interface ShipmentAttempt {
  readonly id: string;
  readonly operation: 'auth' | 'create' | 'track' | 'cancel' | 'pickup';
  readonly outcome: 'success' | 'retryable_error' | 'permanent_error' | 'auth_error';
  readonly httpStatus: number | null;
  readonly carrierErrorCode: string | null;
  readonly durationMs: number | null;
  readonly createdAt: string;
}

export interface ShipmentDetail {
  readonly shipment: Shipment;
  readonly order: {
    readonly id: string;
    readonly number: string;
    readonly status: string;
    readonly total: string;
    readonly recipientName: string;
    readonly governorate: string;
    readonly delegation: string;
  };
  readonly events: readonly ShipmentEvent[];
  readonly attempts: readonly ShipmentAttempt[];
  /** Verbatim, as Jibex wrote them. Their meaning is not confirmed (D4): shown, never computed with. */
  readonly amounts: {
    readonly codAmountSent: string | null;
    readonly carrierPrice: string | null;
    readonly carrierAmountToCollect: string | null;
  } | null;
}

/** Actions the detail panel offers, from the shipment's state (the API re-checks every one). */
export function availableActions(shipment: Shipment): {
  readonly refresh: boolean;
  readonly retry: boolean;
  readonly cancel: boolean;
  readonly resend: boolean;
  readonly editCity: boolean;
} {
  const atCarrier = shipment.trackingNumber !== null;
  return {
    refresh: atCarrier && !['delivered', 'returned', 'cancelled', 'lost'].includes(shipment.status),
    retry: shipment.status === 'dispatch_failed',
    cancel:
      atCarrier && (shipment.status === 'registered' || shipment.status === 'pickup_scheduled'),
    resend: shipment.status === 'cancelled',
    editCity: !atCarrier,
  };
}
